import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiAward,
    FiCreditCard,
    FiCalendar,
    FiAlertCircle,
    FiArrowRight,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import portalService from "../../../services/portalService";
import CollegeLogoBadge from "../../../components/common/CollegeLogoBadge";

function DonutRing({ pct = 0, size = 80, stroke = 8, color = "#003b67" }) {
    const r = (size - stroke * 2) / 2,
        c = 2 * Math.PI * r,
        o = c - (Math.min(pct, 100) / 100) * c;
    return (
        <svg width={size} height={size}>
            <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={`${color}20`}
                strokeWidth={stroke}
            />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={color}
                strokeWidth={stroke}
                strokeDasharray={c}
                strokeDashoffset={o}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: "stroke-dashoffset 0.8s" }}
            />
            <text
                x={size / 2}
                y={size / 2 - 4}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: 14, fontWeight: 800, fill: color }}
            >
                {Math.round(pct)}%
            </text>
            <text
                x={size / 2}
                y={size / 2 + 9}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: 8, fill: "#9ca3af", fontWeight: 600 }}
            >
                present
            </text>
        </svg>
    );
}

const MON = [
    "",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

export default function ParentDashboard() {
    const { user, collegeName } = useApp();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const now = new Date();
        Promise.all([
            portalService.getProfile(),
            portalService.getAttendance(),
            portalService.getResults(),
            portalService.getFees(),
            portalService.getEvents({
                month: now.getMonth() + 1,
                year: now.getFullYear(),
            }),
        ])
            .then(([p, a, r, f, ev]) =>
                setData({
                    profile: p.data,
                    att: a.data,
                    results: r.data || [],
                    fees: f.data,
                    events: ev.data || [],
                }),
            )
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading…
            </div>
        );
    if (!data) return null;

    const { profile, att, results, fees, events } = data;
    const pct = att?.summary?.percentage || 0;
    const attColor = pct >= 75 ? "#16a34a" : pct >= 60 ? "#d97706" : "#dc2626";
    const latest = results[0];
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = events.filter((e) => e.date >= today).slice(0, 3);
    const initials =
        profile?.fullName
            ?.split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((p) => p[0]?.toUpperCase())
            .join("") || "S";

    return (
        <div>
            {/* Header */}
            <div className="page-header" style={{ marginBottom: "1.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <CollegeLogoBadge size={52} />
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#003b67", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {collegeName}
                        </div>
                        <h1 className="page-title" style={{ margin: "2px 0 0 0" }}>Welcome, {user?.displayName} 👋</h1>
                        <p className="page-subtitle" style={{ margin: "2px 0 0 0" }}>Viewing dashboard for your child</p>
                    </div>
                </div>
            </div>

            {/* Child info card */}
            <div
                className="card"
                style={{
                    marginBottom: "1rem",
                    padding: "1.1rem 1.25rem",
                    borderLeft: "4px solid #059669",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <div
                        style={{
                            width: 48,
                            height: 48,
                            borderRadius: 14,
                            overflow: "hidden",
                            background: "#e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 16,
                            fontWeight: 700,
                            color: "#475569",
                            flexShrink: 0,
                        }}
                    >
                        {profile?.studentPhoto?.url ? (
                            <img
                                src={profile.studentPhoto.url}
                                alt={profile.fullName}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                        ) : (
                            initials
                        )}
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                            {profile?.fullName}
                        </div>
                        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                            {profile?.course?.shortName || "Course"} {profile?.yearNumber ? `— Year ${profile?.yearNumber}` : ""} · Enrollment:{" "}
                            {profile?.enrollmentNo}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                            {profile?.academicYear?.label}
                        </div>
                    </div>
                </div>
            </div>

            {/* Attendance warning */}
            {pct > 0 && pct < 75 && (
                <div
                    style={{
                        background: "#fef3c7",
                        border: "1px solid #fcd34d",
                        borderRadius: 8,
                        padding: "0.75rem 1rem",
                        marginBottom: "1rem",
                        fontSize: 13,
                        color: "#92400e",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <FiAlertCircle size={14} />
                    Your child's attendance is <strong>{pct}%</strong> — below required
                    75%. Please encourage regular attendance.
                </div>
            )}

            {/* Stats row */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr 1fr",
                    gap: "1rem",
                    marginBottom: "1rem",
                    alignItems: "start",
                }}
            >
                {/* Attendance donut */}
                <div
                    className="card"
                    style={{
                        padding: "1.1rem",
                        textAlign: "center",
                        cursor: "pointer",
                        minWidth: 140,
                    }}
                    onClick={() => navigate("/parent/attendance")}
                >
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            color: "var(--text-muted)",
                            marginBottom: "0.5rem",
                        }}
                    >
                        Attendance
                    </div>
                    <DonutRing pct={pct} color={attColor} />
                    <div
                        style={{
                            marginTop: "0.5rem",
                            display: "flex",
                            justifyContent: "center",
                            gap: "0.75rem",
                            fontSize: 11.5,
                        }}
                    >
                        <span style={{ color: "#16a34a" }}>
                            <strong>{att?.summary?.present || 0}</strong>P
                        </span>
                        <span style={{ color: "#dc2626" }}>
                            <strong>{att?.summary?.absent || 0}</strong>A
                        </span>
                    </div>
                </div>

                {/* Latest result */}
                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/parent/results")}
                >
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            color: "var(--text-muted)",
                            marginBottom: "0.5rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        <FiAward size={11} /> Latest Result
                    </div>
                    {latest ? (
                        <>
                            <div
                                style={{
                                    fontSize: 24,
                                    fontWeight: 900,
                                    color: latest.result === "Pass" ? "#16a34a" : "#dc2626",
                                }}
                            >
                                {latest.percentage}%
                            </div>
                            <div
                                style={{
                                    fontSize: 12.5,
                                    color: "var(--text-muted)",
                                    marginTop: 2,
                                }}
                            >
                                {latest.exam?.title}
                            </div>
                            <div
                                style={{
                                    fontSize: 11.5,
                                    marginTop: 3,
                                    fontWeight: 600,
                                    color: latest.result === "Pass" ? "#16a34a" : "#dc2626",
                                }}
                            >
                                {latest.result} · Grade {latest.grade}
                            </div>
                        </>
                    ) : (
                        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                            No results yet
                        </div>
                    )}
                    <div
                        style={{
                            fontSize: 11,
                            color: "#003b67",
                            marginTop: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                        }}
                    >
                        View all <FiArrowRight size={10} />
                    </div>
                </div>

                {/* Fee status */}
                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/parent/fees")}
                >
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            color: "var(--text-muted)",
                            marginBottom: "0.5rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        <FiCreditCard size={11} /> Fee Status
                    </div>
                    <div
                        style={{
                            fontSize: 22,
                            fontWeight: 900,
                            color: fees?.pending > 0 ? "#dc2626" : "#16a34a",
                        }}
                    >
                        ₹{fees?.pending?.toLocaleString("en-IN") || "0"}
                    </div>
                    <div
                        style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}
                    >
                        {fees?.pending > 0 ? "Pending" : "Fully paid ✓"}
                    </div>
                    <div
                        style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 3 }}
                    >
                        Paid: ₹{fees?.totalPaid?.toLocaleString("en-IN") || "0"} of ₹
                        {fees?.feesDecided?.toLocaleString("en-IN") || "0"}
                    </div>
                </div>
            </div>

            {/* Upcoming events */}
            {upcoming.length > 0 && (
                <div className="card">
                    <div
                        className="card-header"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <span
                            style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                        >
                            <FiCalendar size={13} /> Upcoming Events
                        </span>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => navigate("/parent/events")}
                            style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
                        >
                            All <FiArrowRight size={11} />
                        </button>
                    </div>
                    {upcoming.map((ev) => (
                        <div
                            key={ev._id}
                            style={{
                                padding: "0.6rem 1.25rem",
                                borderBottom: "1px solid var(--border)",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                            }}
                        >
                            <div
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 9,
                                    background: ev.isHoliday ? "#fee2e2" : "#e8f1f8",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 800,
                                        color: ev.isHoliday ? "#dc2626" : "#003b67",
                                        lineHeight: 1,
                                    }}
                                >
                                    {ev.date.slice(8)}
                                </div>
                                <div style={{ fontSize: 9, color: "var(--text-muted)" }}>
                                    {MON[Number(ev.date.slice(5, 7))]}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{ev.title}</div>
                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                    {ev.type}
                                    {ev.isHoliday ? " · 🏖 Holiday" : ""}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
